/**
 * OpenMeta — Apps API client (Phase 9).
 * Utilise le token JWT créateur (localStorage 'om_creator_token').
 */
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const CREATOR_TOKEN_KEY = 'om_creator_token';

const apps = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  headers: { 'Content-Type': 'application/json' },
});

apps.interceptors.request.use((config) => {
  if (typeof window !== 'undefined') {
    const t = window.localStorage.getItem(CREATOR_TOKEN_KEY);
    if (t) config.headers.Authorization = `Bearer ${t}`;
  }
  return config;
});

function normalizeError(err) {
  const status = err?.response?.status || 0;
  const detail = err?.response?.data?.detail;
  return { status, detail: typeof detail === 'string' ? detail : '' };
}

// ---------------- Créateur ----------------
export async function createApp({ name, vertical, lang, from_concept_id } = {}) {
  try {
    const res = await apps.post('/apps', { name, vertical, lang, from_concept_id });
    return { ok: true, ...res.data };
  } catch (err) {
    return { ok: false, ...normalizeError(err) };
  }
}

export async function listMyApps() {
  try {
    const res = await apps.get('/apps');
    return { ok: true, ...res.data };
  } catch (err) {
    return { ok: false, ...normalizeError(err) };
  }
}

export async function getApp(appId) {
  try {
    const res = await apps.get(`/apps/${encodeURIComponent(appId)}`);
    return { ok: true, ...res.data };
  } catch (err) {
    return { ok: false, ...normalizeError(err) };
  }
}

export async function updateApp(appId, patch) {
  try {
    const res = await apps.patch(`/apps/${encodeURIComponent(appId)}`, patch);
    return { ok: true, ...res.data };
  } catch (err) {
    return { ok: false, ...normalizeError(err) };
  }
}

export async function deleteApp(appId) {
  try {
    const res = await apps.delete(`/apps/${encodeURIComponent(appId)}`);
    return { ok: true, ...res.data };
  } catch (err) {
    return { ok: false, ...normalizeError(err) };
  }
}

export async function publishApp(appId) {
  try {
    const res = await apps.post(`/apps/${encodeURIComponent(appId)}/publish`);
    return { ok: true, ...res.data };
  } catch (err) {
    return { ok: false, ...normalizeError(err) };
  }
}

export async function unpublishApp(appId) {
  try {
    const res = await apps.post(`/apps/${encodeURIComponent(appId)}/unpublish`);
    return { ok: true, ...res.data };
  } catch (err) {
    return { ok: false, ...normalizeError(err) };
  }
}

export async function listSubmissions(appId, { page = 1, limit = 50 } = {}) {
  try {
    const res = await apps.get(`/apps/${encodeURIComponent(appId)}/submissions`, {
      params: { page, limit },
    });
    return { ok: true, ...res.data };
  } catch (err) {
    return { ok: false, ...normalizeError(err) };
  }
}

// Activity 7 days (Phase 10)
export async function getAppActivity(appId) {
  try {
    const res = await apps.get(`/apps/${encodeURIComponent(appId)}/activity`);
    return { ok: true, ...res.data };
  } catch (err) {
    return { ok: false, ...normalizeError(err) };
  }
}

// Admin apps moderation (Phase 10) — token = admin JWT
function adminHeaders() {
  const t = typeof window !== 'undefined' ? window.localStorage.getItem('om_admin_token') : null;
  return t ? { Authorization: `Bearer ${t}` } : {};
}

export async function adminListApps({ q = '', status = '', flagged = null, page = 1, limit = 50 } = {}) {
  try {
    const params = { page, limit };
    if (q) params.q = q;
    if (status) params.status = status;
    if (flagged !== null) params.flagged = flagged;
    const res = await apps.get('/admin/apps', { params, headers: adminHeaders() });
    return { ok: true, ...res.data };
  } catch (err) {
    return { ok: false, ...normalizeError(err) };
  }
}

export async function adminModerateApp(appId, reason) {
  try {
    const res = await apps.post(
      `/admin/apps/${encodeURIComponent(appId)}/moderate`,
      { reason },
      { headers: adminHeaders() }
    );
    return { ok: true, app: res.data };
  } catch (err) {
    return { ok: false, ...normalizeError(err) };
  }
}

export async function adminUnflagApp(appId) {
  try {
    const res = await apps.post(
      `/admin/apps/${encodeURIComponent(appId)}/unflag`,
      {},
      { headers: adminHeaders() }
    );
    return { ok: true, app: res.data };
  } catch (err) {
    return { ok: false, ...normalizeError(err) };
  }
}

export function submissionsExportUrl(appId) {
  return `${BACKEND_URL}/api/apps/${encodeURIComponent(appId)}/submissions/export`;
}

export async function downloadSubmissionsCsv(appId, slug) {
  const res = await apps.get(`/apps/${encodeURIComponent(appId)}/submissions/export`, {
    responseType: 'blob',
  });
  const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `openmeta-${slug || appId}-soumissions.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ---------------- Public (no auth) ----------------
const publicClient = axios.create({
  baseURL: `${BACKEND_URL}/api/public`,
  headers: { 'Content-Type': 'application/json' },
});

export async function fetchPublicApp(slug) {
  try {
    const res = await publicClient.get(`/apps/${encodeURIComponent(slug)}`);
    return { ok: true, app: res.data };
  } catch (err) {
    const status = err?.response?.status;
    if (status === 404) return { ok: false, code: 'not_found' };
    return { ok: false, code: 'error' };
  }
}

export async function sendPublicChat(slug, messages) {
  try {
    const res = await publicClient.post(`/apps/${encodeURIComponent(slug)}/chat`, { messages });
    return { ok: true, reply: res.data.reply };
  } catch (err) {
    const status = err?.response?.status;
    if (status === 429) return { ok: false, code: 'rate_limit' };
    if (status === 503) return { ok: false, code: 'unavailable' };
    if (status === 404) return { ok: false, code: 'not_found' };
    return { ok: false, code: 'error' };
  }
}

export async function submitPublicForm(slug, block_id, fields) {
  try {
    const res = await publicClient.post(`/apps/${encodeURIComponent(slug)}/submit`, {
      block_id,
      fields,
    });
    return { ok: true, success_message: res.data.success_message };
  } catch (err) {
    const status = err?.response?.status;
    const detail = err?.response?.data?.detail || '';
    if (status === 400) return { ok: false, code: 'invalid', detail };
    if (status === 429) return { ok: false, code: 'rate_limit' };
    if (status === 404) return { ok: false, code: 'not_found' };
    return { ok: false, code: 'error' };
  }
}
